import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { 
  AlertTriangle, AlertCircle, Clock, CheckCircle2, RefreshCw, 
  Users, ChevronRight, Shield, FileText, Bell, ArrowUpRight,
  UserCheck, History
} from "lucide-react";
import { toast } from "sonner";

export default function EscalationsDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("active");
  const [escalations, setEscalations] = useState([]);
  const [summary, setSummary] = useState({ total_active: 0, level_1: 0, level_2: 0, level_3: 0, level_4: 0 });
  const [auditLogs, setAuditLogs] = useState([]);
  const [selectedEscalation, setSelectedEscalation] = useState(null);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState("");

  const allowedRoles = ["admin", "administrator", "supervisor", "coordinator"];

  useEffect(() => {
    if (authLoading) return;
    if (!user || !allowedRoles.includes(user?.role?.toLowerCase())) {
      toast.error("Access denied - Admin/Supervisor access required");
      navigate("/");
      return;
    }
    loadData();
  }, [user, authLoading, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchEscalations(), fetchAuditLogs()]);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEscalations = async () => {
    try {
      const response = await api.get("/escalations/dashboard");
      setEscalations(response.data.escalations || []);
      setSummary(response.data.summary || { total_active: 0, level_1: 0, level_2: 0, level_3: 0, level_4: 0 });
    } catch (error) {
      console.error("Error fetching escalations:", error);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const response = await api.get("/escalations/compliance/audit-log?limit=50");
      setAuditLogs(response.data.logs || []);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
    }
  };

  const acknowledgeEscalation = async (escalationId) => {
    try {
      await api.put(`/escalations/${escalationId}/acknowledge`);
      toast.success("Escalation acknowledged");
      fetchEscalations();
    } catch (error) {
      toast.error("Failed to acknowledge escalation");
    }
  };

  const resolveEscalation = async () => {
    if (!selectedEscalation || !resolutionNotes.trim()) {
      toast.error("Please provide resolution notes");
      return;
    }
    try {
      await api.put(`/escalations/${selectedEscalation.id}/resolve?resolution_notes=${encodeURIComponent(resolutionNotes)}`);
      toast.success("Escalation resolved");
      setShowResolveDialog(false);
      setSelectedEscalation(null);
      setResolutionNotes("");
      fetchEscalations();
      fetchAuditLogs();
    } catch (error) {
      toast.error("Failed to resolve escalation");
    }
  };

  const triggerProcessing = async () => {
    try {
      await api.post("/escalations/process");
      toast.success("Escalation processing triggered");
      setTimeout(fetchEscalations, 2000);
    } catch (error) {
      toast.error("Failed to trigger processing");
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case "level_1": return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
      case "level_2": return "bg-orange-500/20 text-orange-400 border-orange-500/30";
      case "level_3": return "bg-red-500/20 text-red-400 border-red-500/30";
      case "level_4": return "bg-red-600/20 text-red-300 border-red-600/30";
      default: return "bg-slate-500/20 text-slate-400 border-slate-500/30";
    }
  };

  const getLevelLabel = (level) => {
    switch (level) {
      case "level_1": return "Level 1 - Staff";
      case "level_2": return "Level 2 - Supervisor";
      case "level_3": return "Level 3 - Dept Head";
      case "level_4": return "Level 4 - Compliance";
      default: return level;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Active</Badge>;
      case "acknowledged":
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Acknowledged</Badge>;
      case "escalated":
        return <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">Escalated</Badge>;
      case "resolved":
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Resolved</Badge>;
      case "reassigned":
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Reassigned</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white" data-testid="escalations-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-orange-600 py-6 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <AlertTriangle className="h-7 w-7" />
              Escalation Management
            </h1>
            <p className="text-red-100 mt-1 text-sm">
              Automatic escalation workflows with compliance audit trail
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              onClick={loadData}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button 
              className="bg-white text-red-700 hover:bg-red-50"
              onClick={triggerProcessing}
            >
              <Bell className="h-4 w-4 mr-2" />
              Process Escalations
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Active</p>
                  <p className="text-3xl font-bold text-white">{summary.total_active}</p>
                </div>
                <div className="h-12 w-12 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-red-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Level 1</p>
                  <p className="text-3xl font-bold text-yellow-400">{summary.level_1}</p>
                </div>
                <div className="h-12 w-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                  <Clock className="h-6 w-6 text-yellow-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Level 2</p>
                  <p className="text-3xl font-bold text-orange-400">{summary.level_2}</p>
                </div>
                <div className="h-12 w-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-orange-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Level 3</p>
                  <p className="text-3xl font-bold text-red-400">{summary.level_3}</p>
                </div>
                <div className="h-12 w-12 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <ArrowUpRight className="h-6 w-6 text-red-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Level 4</p>
                  <p className="text-3xl font-bold text-red-300">{summary.level_4}</p>
                </div>
                <div className="h-12 w-12 bg-red-600/20 rounded-lg flex items-center justify-center">
                  <Shield className="h-6 w-6 text-red-300" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-slate-800/50 border border-slate-700">
            <TabsTrigger value="active" className="data-[state=active]:bg-red-600">
              Active Escalations ({summary.total_active})
            </TabsTrigger>
            <TabsTrigger value="audit" className="data-[state=active]:bg-red-600">
              Compliance Audit Log
            </TabsTrigger>
            <TabsTrigger value="rules" className="data-[state=active]:bg-red-600">
              Escalation Rules
            </TabsTrigger>
          </TabsList>

          {/* Active Escalations Tab */}
          <TabsContent value="active" className="space-y-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                  Active Escalations
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Overdue tasks requiring immediate attention
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {escalations.map((esc) => (
                    <div key={esc.id} className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-lg ${getLevelColor(esc.level).split(' ')[0]}`}>
                            <AlertCircle className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{esc.source_title}</p>
                            <p className="text-slate-400 text-sm">{esc.patient_name}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className={getLevelColor(esc.level)}>{getLevelLabel(esc.level)}</Badge>
                              {getStatusBadge(esc.status)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-red-400 font-bold">{esc.hours_overdue?.toFixed(1)}h overdue</p>
                            <p className="text-slate-400 text-xs">Original: {esc.original_assignee_name || "Unassigned"}</p>
                            {esc.backup_assignee_name && (
                              <p className="text-blue-400 text-xs">Backup: {esc.backup_assignee_name}</p>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            {esc.status === "active" && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-yellow-400 border-yellow-500/30"
                                onClick={() => acknowledgeEscalation(esc.id)}
                              >
                                <CheckCircle2 className="h-4 w-4 mr-1" />
                                Acknowledge
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => { setSelectedEscalation(esc); setShowResolveDialog(true); }}
                            >
                              <CheckCircle2 className="h-4 w-4 mr-1" />
                              Resolve
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Audit Trail */}
                      {esc.audit_trail && esc.audit_trail.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-700">
                          <p className="text-slate-400 text-xs mb-2 flex items-center gap-1">
                            <History className="h-3 w-3" /> Audit Trail
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {esc.audit_trail.slice(-3).map((entry, idx) => (
                              <span key={idx} className="text-xs bg-slate-800 px-2 py-1 rounded">
                                {entry.action} - {formatDate(entry.timestamp)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {escalations.length === 0 && (
                    <div className="text-center py-8">
                      <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                      <p className="text-green-400 font-medium">No Active Escalations</p>
                      <p className="text-slate-400 text-sm">All tasks are on track!</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compliance Audit Log Tab */}
          <TabsContent value="audit" className="space-y-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Shield className="h-5 w-5 text-blue-400" />
                  Compliance Audit Trail
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Immutable record of all escalation events for regulatory compliance
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {auditLogs.map((log) => (
                    <div key={log.id} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          log.severity === "critical" ? "bg-red-500/20" :
                          log.severity === "warning" ? "bg-yellow-500/20" :
                          "bg-blue-500/20"
                        }`}>
                          {log.severity === "critical" ? (
                            <AlertCircle className="h-4 w-4 text-red-400" />
                          ) : log.severity === "warning" ? (
                            <AlertTriangle className="h-4 w-4 text-yellow-400" />
                          ) : (
                            <FileText className="h-4 w-4 text-blue-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-white text-sm">{log.description}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {log.patient_name && (
                              <span className="text-slate-400 text-xs">Patient: {log.patient_name}</span>
                            )}
                            {log.regulatory_reference && (
                              <span className="text-cyan-400 text-xs">{log.regulatory_reference}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="text-xs">{log.event_type}</Badge>
                        <p className="text-slate-500 text-xs mt-1">{formatDate(log.created_at)}</p>
                      </div>
                    </div>
                  ))}
                  {auditLogs.length === 0 && (
                    <p className="text-slate-400 text-center py-8">No audit log entries</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Escalation Rules Tab */}
          <TabsContent value="rules" className="space-y-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Escalation Rules</CardTitle>
                <CardDescription className="text-slate-400">
                  Configure automatic escalation thresholds and actions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                  <h3 className="text-white font-medium mb-4">Default Escalation Rule</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 bg-slate-800 rounded-lg">
                      <p className="text-slate-400 text-xs">Trigger After</p>
                      <p className="text-yellow-400 font-bold">4 hours</p>
                      <p className="text-slate-500 text-xs">overdue</p>
                    </div>
                    <div className="p-3 bg-slate-800 rounded-lg">
                      <p className="text-slate-400 text-xs">Level 2 (Supervisor)</p>
                      <p className="text-orange-400 font-bold">+8 hours</p>
                      <p className="text-slate-500 text-xs">after L1</p>
                    </div>
                    <div className="p-3 bg-slate-800 rounded-lg">
                      <p className="text-slate-400 text-xs">Level 3 (Dept Head)</p>
                      <p className="text-red-400 font-bold">+24 hours</p>
                      <p className="text-slate-500 text-xs">after L1</p>
                    </div>
                    <div className="p-3 bg-slate-800 rounded-lg">
                      <p className="text-slate-400 text-xs">Level 4 (Compliance)</p>
                      <p className="text-red-300 font-bold">+48 hours</p>
                      <p className="text-slate-500 text-xs">after L1</p>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-400" />
                      <span className="text-slate-300 text-sm">Notify Supervisor</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-400" />
                      <span className="text-slate-300 text-sm">Create Backup Assignment</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-400" />
                      <span className="text-slate-300 text-sm">Log to Compliance</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-400" />
                      <span className="text-slate-300 text-sm">Send Email Alerts</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Resolve Dialog */}
      <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Resolve Escalation</DialogTitle>
            <DialogDescription className="text-slate-400">
              Provide resolution notes for the compliance audit trail
            </DialogDescription>
          </DialogHeader>
          {selectedEscalation && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-900 rounded-lg">
                <p className="text-white font-medium">{selectedEscalation.source_title}</p>
                <p className="text-slate-400 text-sm">{selectedEscalation.patient_name}</p>
              </div>
              <div>
                <label className="text-white text-sm font-medium">Resolution Notes *</label>
                <Textarea 
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="Describe how this escalation was resolved..."
                  className="mt-2 bg-slate-900 border-slate-700 text-white"
                  rows={4}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolveDialog(false)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={resolveEscalation}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Resolve Escalation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
